//
//  CanonInterview.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  The nine-question canon interview and a stored canon identity record. Lifted out of
//  Birth.tsx unchanged, so /birth and the per-agent answers panel share one copy. The
//  Birth action shows only where the caller passes `onBirth`.
//
import type { CanonBirth, CanonBirthExistsDetails, CanonPreview, CanonSectors } from '@shared/api/types'
import { atLeast, type TenantRole } from '@shared/auth'
import { href } from '@shared/router'

export function CanonInterview({ sectors, sector, sectorId, onSector, answers, onAnswer, canon, role, onBirth, birthBusy, birth, sectorOptional }: {
  sectors: CanonSectors | 'unavailable' | null
  sector: CanonSectors['sectors'][number] | null
  sectorId: string
  onSector: (s: string) => void
  answers: string[]
  onAnswer: (i: number, v: string) => void
  canon: CanonPreview | 'unavailable' | null
  role: TenantRole
  onBirth?: () => void
  birthBusy?: boolean
  birth?: CanonBirth | 'forbidden' | 'unavailable' | CanonBirthExistsDetails | null
  /** When set, the sector starts empty ("Choose a sector") and the nine questions appear only after one is chosen. */
  sectorOptional?: boolean
}) {
  if (sectors === null) return <p className="hint">Loading canon sectors…</p>
  if (sectors === 'unavailable') return <p className="hint">The canon service isn't reachable right now.</p>
  if (!sector && !sectorOptional) return <p className="hint">No canon sectors returned.</p>
  return (
    <>
      <label htmlFor="c-sector">Sector</label>
      <select id="c-sector" value={sectorId} onChange={(e) => onSector(e.target.value)}>
        {sectorOptional && <option value="">Choose a sector…</option>}
        {sectors.sectors.map((s) => <option key={s.sector} value={s.sector}>{s.sector}</option>)}
      </select>
      {sector && sectors.axes.map((axis, i) => (
        <div key={axis.id}>
          <label htmlFor={`c-${axis.id}`}>{sectors.questions[i]} <span className="hint">({axis.name}: {axis.low} ↔ {axis.high})</span></label>
          <select id={`c-${axis.id}`} value={answers[i] ?? ''} onChange={(e) => onAnswer(i, e.target.value)}>
            <option value="">— choose —</option>
            {sector.scales[i]?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      ))}
      {canon === 'unavailable' && <p className="hint">The canon preview couldn't be computed for these answers.</p>}
      {canon && canon !== 'unavailable' && (
        <>
          <h2>Live weights, cast &amp; tripwire</h2>
          <p className="hint">{canon.labels.k}: k={canon.k}. {canon.labels.tripwire}: {canon.tripwire}. {canon.labels.risk_slope}: {canon.risk_slope}. {canon.labels.corpus}.</p>
          {canon.record.status === 'QUARANTINE-REVIEW' && <p className="hint" style={{ color: 'var(--watch)' }} role="status">Tripwire tripped -- QUARANTINE-REVIEW. Not a failure; a human decides before this goes further.</p>}
          <div className="grid">
            <div className="card"><div className="l">Tripwire margin</div><div className="v" style={{ color: canon.record.genesis.adversarial_tripwire.tripped ? 'var(--deny)' : 'var(--ok)' }}>{canon.record.genesis.adversarial_tripwire.margin.toFixed(3)}</div></div>
            <div className="card"><div className="l">Dominant range</div><div className="v">{canon.record.genesis.dominant_range}</div></div>
            <div className="card"><div className="l">Corpus (labeled)</div><div className="v">{canon.record.genesis.corpus_size} rows -- {canon.labels.corpus}</div></div>
          </div>
          <table>
            <thead><tr><th>Axis</th><th>Answer</th><th>Weight ({canon.labels.base})</th></tr></thead>
            <tbody>
              {canon.axes.map((a) => <tr key={a.id}><td className="mono">{a.name}</td><td>{a.answer}</td><td className="mono">{a.weight.toFixed(3)}</td></tr>)}
            </tbody>
          </table>
          <h2>Birth graph <span className="hint">(2D projection, {canon.labels.corpus})</span></h2>
          <svg viewBox="-1.1 -1.1 2.2 2.2" width={280} height={280} style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 10 }}>
            {canon.projection.points.map((p) => (
              <circle key={p.id} cx={p.xy[0]} cy={-p.xy[1]} r={p.cast ? 0.035 : 0.015}
                fill={p.adversarial_nearest ? 'var(--deny)' : p.cast ? 'var(--gold)' : 'var(--dim)'}>
                <title>{p.role} ({p.id})</title>
              </circle>
            ))}
            <circle cx={canon.projection.seed[0]} cy={-canon.projection.seed[1]} r={0.04} fill="var(--ok)"><title>seed</title></circle>
          </svg>

          {onBirth && (
            <>
              <h2>Birth</h2>
              {atLeast(role, 'security-admin') ? (
                <p><button className="btn" disabled={birthBusy} onClick={onBirth}>{birthBusy ? 'Birthing…' : 'Birth this canon identity'}</button></p>
              ) : (
                <p className="hint">Only owner/security-admin may store a canon birth record -- your role ({role}) may preview but not birth.</p>
              )}
              {birth === 'forbidden' && <p className="err" role="alert">403 forbidden -- your role may not birth a canon identity.</p>}
              {birth === 'unavailable' && <p className="err" role="alert">The birth couldn't be stored. Check the answers and try again.</p>}
              {birth && typeof birth === 'object' && 'existing_id' in birth && (
                <p className="hint" role="status">
                  Already born as <span className="mono">{birth.rn_id}</span> -- these exact answers were recorded before, so nothing new was stored.{' '}
                  <a href={href(`/births?id=${birth.existing_id}`)}>See that record ›</a>
                </p>
              )}
            </>
          )}
          {birth && typeof birth === 'object' && 'record' in birth && <CanonBirthPanel birth={birth} />}
        </>
      )}
    </>
  )
}

/** The stored record from `POST /v1/canon/births` (§6a) -- rn_id, status, genesis_seal, tripwire
 * margin, and all four canon labels verbatim. A QUARANTINE-REVIEW status is stored, not refused,
 * and is shown as a review state, never as a failure. */
export function CanonBirthPanel({ birth }: { birth: CanonBirth }) {
  const quarantined = birth.record.status === 'QUARANTINE-REVIEW'
  return (
    <div className="card" role="status">
      <div className="l">Stored canon identity</div>
      <p className="mono">{birth.record.rn_id}</p>
      <p>
        Status: {quarantined
          ? <span className="pill off">QUARANTINE-REVIEW</span>
          : <span className="pill ok">identity-bearing</span>}
      </p>
      {quarantined && <p className="hint" style={{ color: 'var(--watch)' }}>Stored as computed -- not a failure. A human decides before this goes further (Step 4).</p>}
      <p className="hint">Genesis seal: <span className="mono">{birth.record.genesis_seal}</span></p>
      <p className="hint">Tripwire margin: <span className="mono">{birth.record.genesis.adversarial_tripwire.margin.toFixed(3)}</span></p>
      <p className="hint">{birth.labels.k} · {birth.labels.tripwire} · {birth.labels.risk_slope} · {birth.labels.base} · {birth.labels.corpus}</p>
    </div>
  )
}
